<script setup lang="ts">
import { useLogger } from "@blackfisk/vue/composables/useLogger";
import { pwaInfo } from "virtual:pwa-info";
import { useRegisterSW } from "virtual:pwa-register/vue";
import { onBeforeUnmount } from "vue";

const logger = useLogger("client-frontend", "pwa");
const isLocalEnvironment = import.meta.env.VITE_ENVIRONMENT === "local";
if (isLocalEnvironment) {
  logger.debug({ pwaInfo });
}

const CHECKING_INTERVAL_MS = 60 * 1000;
const VISIBILITY_UPDATE_MS = 60 * 1000;

let swIntervalId: ReturnType<typeof setInterval> | null = null;
let lastVisibilityCheck = 0;

const { offlineReady, needRefresh, updateServiceWorker } = useRegisterSW({
  immediate: true,
  onRegisteredSW(swUrl, r) {
    if (isLocalEnvironment) {
      logger.debug(`Service Worker at: ${swUrl}`);
    }

    // Tab-focus trigger — efficient update detection for all users
    const onVisibilityChange = (): void => {
      if (document.visibilityState === "visible" && r) {
        const now = Date.now();
        if (now - lastVisibilityCheck > VISIBILITY_UPDATE_MS) {
          lastVisibilityCheck = now;
          r.update();
        }
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    // Check on initial registration
    r?.update();

    // Diagnostic: log when browser detects a new SW version
    r?.addEventListener("updatefound", () => {
      if (isLocalEnvironment) {
        logger.debug("[PWA] Browser detected new SW version", {
          installing: r?.installing,
          state: r?.installing?.state,
        });
      }
    });

    // Background polling — catches mid-session deploys
    swIntervalId = setInterval(async () => {
      if (isLocalEnvironment) {
        logger.debug("Checking for sw update");
      }
      await r?.update();
    }, CHECKING_INTERVAL_MS);
  },
});

onBeforeUnmount(() => {
  if (swIntervalId !== null) {
    clearInterval(swIntervalId);
    swIntervalId = null;
  }
});

const handleSWUpdate = (): void => {
  updateServiceWorker();
  setTimeout(() => window.location.reload(), 3000);
};

const close = (): void => {
  offlineReady.value = false;
  needRefresh.value = false;
};
</script>

<template>
  <div v-if="needRefresh" class="pwa-toast bf-background-dark" role="alert">
    <div class="message">New version available. Click reload to update.</div>
    <div class="flex flex-row gap-2">
      <button class="reload-button" @click="handleSWUpdate">Reload</button>
      <button class="close-button" @click="close">Dismiss</button>
    </div>
  </div>
</template>

<style scoped>
.pwa-toast {
  position: fixed;
  right: 0;
  bottom: 0;
  margin: 16px;
  margin-bottom: calc(16px + env(safe-area-inset-bottom, 0px));
  padding: 12px;
  border: 1px solid #8885;
  border-radius: 4px;
  z-index: 99999;
  text-align: left;
  box-shadow: 3px 4px 5px 0 #8885;
}
.pwa-toast .message {
  margin-bottom: 8px;
}
.pwa-toast .reload-button {
  @apply cursor-pointer rounded bg-primary-500 px-3 py-1 text-sm text-primary-950;
}
.pwa-toast .close-button {
  @apply cursor-pointer rounded border border-surface-300 bg-transparent px-3 py-1 text-sm text-surface-700 dark:border-surface-600 dark:text-surface-300;
}
</style>
